import React, { useEffect, useState } from "react";
import { ImageIcon, Play, ExternalLink } from "lucide-react";
import { getMedia } from "@/lib/rexxponsApi";

const HeadingRow = () => (
  <div className="flex items-center gap-3 mb-8">
    <span className="font-sans text-xs text-primary tracking-[0.2em]">06</span>
    <ImageIcon strokeWidth={1.5} className="w-4 h-4 text-primary" />
    <h2 className="font-serif font-medium text-2xl sm:text-3xl tracking-tight">Related Media</h2>
  </div>
);

const VideoCard = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      data-testid="media-video"
      className="group relative rounded-lg overflow-hidden border border-border bg-secondary/40 aspect-video"
    >
      {playing ? (
        <iframe
          title={video.title || "Related video"}
          src={`${video.embed_url}?autoplay=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      ) : (
        <button
          data-testid="media-video-play"
          onClick={() => setPlaying(true)}
          className="w-full h-full block relative"
          aria-label={`Play ${video.title || "video"}`}
        >
          <img
            src={video.thumbnail}
            alt={video.title || ""}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
          />
          <span className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/90 text-primary-foreground shadow-lg">
              <Play strokeWidth={2} className="w-5 h-5 ml-0.5" fill="currentColor" />
            </span>
          </span>
          {video.title && (
            <span className="absolute bottom-0 inset-x-0 p-2.5 text-[11px] leading-snug text-white bg-gradient-to-t from-black/80 to-transparent text-left line-clamp-2">
              {video.title}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export const MediaGallery = ({ query }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMedia(query)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [query]);

  if (loading) {
    return (
      <section id="media" className="scroll-mt-28 py-20" data-testid="media-loading">
        <HeadingRow />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="aspect-square rounded-lg bg-secondary/50 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  const images = data?.images || [];
  const videos = data?.videos || [];
  if (images.length === 0 && videos.length === 0) return null;

  return (
    <section id="media" className="scroll-mt-28 py-20" data-testid="media-gallery">
      <HeadingRow />

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
          {images.map((img, i) => (
            <a
              key={`${i}-${img.url}`}
              data-testid="media-image"
              href={img.landing || img.url}
              target="_blank"
              rel="noopener noreferrer"
              title={img.title || img.creator || "Open source"}
              className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary/40"
            >
              <img
                src={img.thumbnail}
                alt={img.title || ""}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
              <ExternalLink strokeWidth={1.5} className="absolute top-2 right-2 w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          ))}
        </div>
      )}

      {videos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      )}

      {data?.youtube_search_url && (
        <a
          data-testid="media-youtube-link"
          href={data.youtube_search_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors"
        >
          More videos on YouTube <ExternalLink strokeWidth={1.5} className="w-3.5 h-3.5" />
        </a>
      )}
      <p className="font-sans text-xs text-muted-foreground/60 mt-6 leading-relaxed">
        Images via Openverse (openly-licensed) · Videos via YouTube. Media is topic-matched and may not be individually vetted — verify before citing.
      </p>
    </section>
  );
};
